import React, { FC } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import { Box, Text, IconButton, CloseButton } from '@wix/design-system';
import { ArrowLeft } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import s from './MobileFullScreenModalLayout.module.scss';

export type MobileFullScreenModalVariant = 'legacy' | 'contained';

type Props = {
  title: string;
  content: JSX.Element;
  dataHook?: string;
  onClose?: () => void;
  onBack?: () => void;
  footer: JSX.Element;
  /** @default 'legacy' — existing templates/explore behavior */
  variant?: MobileFullScreenModalVariant;
};

export const fullScreenModalDataHooks = {
  layoutDefault: 'mobile-filter-content',
  backButton: 'modal-back-btn',
  title: 'mobile-filter-modal-title',
  closeButton: 'close-filter-modal',
} as const;

const MobileFullScreenModalLayout: FC<Props> = ({
  title,
  content,
  dataHook,
  onClose,
  onBack,
  footer,
  variant = 'legacy',
}) => {
  const { isIntersecting, ref } = useIntersectionObserver({});

  if (variant === 'contained') {
    return (
      <Box
        dataHook={dataHook || fullScreenModalDataHooks.layoutDefault}
        direction="vertical"
        position="fixed"
        top={0}
        left={0}
        width="100%"
        height="100dvh"
        backgroundColor="#ffffff"
        overflow="hidden"
        zIndex={10000}
      >
        <ModalHeader
          title={title}
          onClose={onClose}
          onBack={onBack}
          hasShadow={!isIntersecting}
          headerWrapper="box"
        />
        <Box
          flex="1 1 auto"
          minHeight={0}
          overflowY="auto"
          overscrollBehavior="contain"
        >
          <Box ref={ref} height={0} />
          {content}
        </Box>
        <Box>{footer}</Box>
      </Box>
    );
  }

  return (
    <Box
      dataHook={dataHook || fullScreenModalDataHooks.layoutDefault}
      direction="vertical"
      className={s.modal}
    >
      <ModalHeader
        title={title}
        onClose={onClose}
        onBack={onBack}
        hasShadow={!isIntersecting}
        headerWrapper="div"
      />
      <div className={s.modal__content}>
        <div ref={ref} />
        {content}
      </div>
      <div>{footer}</div>
    </Box>
  );
};

const ModalHeader: FC<
  Pick<Props, 'onBack' | 'title' | 'onClose'> & {
    hasShadow: boolean;
    headerWrapper: 'div' | 'box';
  }
> = ({ onBack, title, onClose, hasShadow, headerWrapper }) => {
  const { t } = useTranslation();
  const shadowStyle = hasShadow
    ? '0px 0px 6px 0px rgba(0, 6, 36, 0.12), 0px 2px 4px 0px rgba(0, 6, 36, 0.12)'
    : undefined;

  const headerContent = (
    <Box
      width={headerWrapper === 'box' ? '100%' : undefined}
      paddingLeft={onBack ? '14px' : '24px'}
      paddingRight="24px"
      paddingTop={onBack ? '15px' : '18px'}
      paddingBottom="12px"
      align="space-between"
    >
      <Box verticalAlign="middle">
        {onBack && (
          <Box paddingRight="12px">
            <IconButton
              skin="light"
              onClick={() => onBack()}
              dataHook={fullScreenModalDataHooks.backButton}
              ariaLabel={t('marketplace-aria-label-modal-back-btn')}
            >
              <ArrowLeft color="#000000" />
            </IconButton>
          </Box>
        )}
        <Text
          tabIndex={0}
          weight="bold"
          dataHook={fullScreenModalDataHooks.title}
        >
          {title}
        </Text>
      </Box>
      {onClose && (
        <CloseButton
          skin="dark"
          dataHook={fullScreenModalDataHooks.closeButton}
          size="large"
          onClick={() => onClose()}
        />
      )}
    </Box>
  );

  if (headerWrapper === 'box') {
    return (
      <Box flexShrink={0} width="100%" boxShadow={shadowStyle}>
        {headerContent}
      </Box>
    );
  }

  return <div style={{ boxShadow: shadowStyle }}>{headerContent}</div>;
};

export default MobileFullScreenModalLayout;
